import { useCallback, useMemo, useState } from 'react';

import type { Principal, Role } from '../../../commonTypes.tsx';
import type { SpecificAccessDetailsProps } from '../specificAccessDetails/SpecificAccessDetails.tsx';

import type { SpecificAccessLiveMutationArgs } from './specificAccessDialogTypes.ts';
import { SpecificAccessFilterType } from './SpecificAccessFilter.tsx';
import {
	reassignPrincipalInLiveDelta,
	removeAllPrincipalsFromLiveDelta,
	removePrincipalFromLiveDelta,
	type SpecificAccessLiveDelta,
} from './specificAccessDialogLiveState.ts';

export type SetLiveSpecificAccessDelta = React.Dispatch<
	React.SetStateAction<SpecificAccessLiveDelta>
>;
export type SetSavingPrincipalIds = React.Dispatch<React.SetStateAction<Set<string>>>;

function shouldIncludePrincipalByAccessType(
	principalType: Principal['type'],
	filterType: SpecificAccessFilterType,
): boolean {
	if (filterType === SpecificAccessFilterType.ALL) {
		return true;
	}

	switch (filterType) {
		case SpecificAccessFilterType.USERS:
			return principalType === 'user';
		case SpecificAccessFilterType.TEAMS:
			return principalType === 'team' || principalType === 'group';
		case SpecificAccessFilterType.GUESTS:
			return principalType === 'external_user';
		case SpecificAccessFilterType.APPS:
			// TODO: update when we have app type
			return false;
		default:
			return true;
	}
}

const isPartialMatch = (query: string, text: string) =>
	text.toLowerCase().includes(query.toLowerCase());

export function useSpecificAccessFilter({
	specificAccessDetails,
}: {
	specificAccessDetails?: SpecificAccessDetailsProps;
}): {
	filterText: string;
	setFilterText: (text: string) => void;
	selectedAccessType: SpecificAccessFilterType;
	setSelectedAccessType: (type: SpecificAccessFilterType) => void;
	filteredSpecificAccessDetails?: SpecificAccessDetailsProps;
} {
	const [filterText, setFilterText] = useState('');
	const [selectedAccessType, setSelectedAccessType] = useState<SpecificAccessFilterType>(
		SpecificAccessFilterType.ALL,
	);

	const filteredSpecificAccessDetails = useMemo((): SpecificAccessDetailsProps | undefined => {
		if (!specificAccessDetails) {
			return undefined;
		}

		const query = filterText.trim();

		return {
			...specificAccessDetails,
			principals: (specificAccessDetails.principals || []).filter((principal) => {
				const principalName = principal?.principalDetails.principal.name;
				if (query !== '' && principalName && !isPartialMatch(query, principalName)) {
					return false;
				}

				const principalType = principal?.principalDetails.principal.type;
				if (!principalType) {
					return true;
				}

				return shouldIncludePrincipalByAccessType(principalType, selectedAccessType);
			}),
		};
	}, [filterText, selectedAccessType, specificAccessDetails]);

	return {
		filterText,
		setFilterText,
		selectedAccessType,
		setSelectedAccessType,
		filteredSpecificAccessDetails,
	};
}

export function useLiveSpecificAccessActions({
	setLiveSpecificAccessDelta,
	setSavingPrincipalIds,
	triggerLiveMutation,
}: {
	setLiveSpecificAccessDelta: SetLiveSpecificAccessDelta;
	setSavingPrincipalIds: SetSavingPrincipalIds;
	triggerLiveMutation: (args: SpecificAccessLiveMutationArgs) => Promise<void>;
}): {
	onChangeRole: (principalId: string, nextRole: Role, previousRole: Role) => Promise<void>;
	onRemovePrincipal: (principalId: string) => Promise<void>;
	onRemoveAllPrincipals: (args: { currentUserId: string; principalIds: string[] }) => Promise<void>;
} {
	const withSaving = useCallback(
		async (principalId: string, fn: () => Promise<void>) => {
			setSavingPrincipalIds((prev) => {
				if (prev.has(principalId)) {
					return prev;
				}
				const next = new Set(prev);
				next.add(principalId);
				return next;
			});

			try {
				await fn();
			} finally {
				setSavingPrincipalIds((prev) => {
					if (!prev.has(principalId)) {
						return prev;
					}
					const next = new Set(prev);
					next.delete(principalId);
					return next;
				});
			}
		},
		[setSavingPrincipalIds],
	);

	const onChangeRole = useCallback(
		async (principalId: string, nextRole: Role, previousRole: Role) => {
			setLiveSpecificAccessDelta((prev) =>
				reassignPrincipalInLiveDelta({ liveDelta: prev, principalId, role: nextRole }),
			);

			await withSaving(principalId, () =>
				triggerLiveMutation({ type: 'change-role', principalId, previousRole, nextRole }),
			);
		},
		[setLiveSpecificAccessDelta, triggerLiveMutation, withSaving],
	);

	const onRemovePrincipal = useCallback(
		async (principalId: string) => {
			setLiveSpecificAccessDelta((prev) =>
				removePrincipalFromLiveDelta({ liveDelta: prev, principalId }),
			);

			await withSaving(principalId, () => triggerLiveMutation({ type: 'remove', principalId }));
		},
		[setLiveSpecificAccessDelta, triggerLiveMutation, withSaving],
	);

	const onRemoveAllPrincipals = useCallback(
		async ({ currentUserId, principalIds }: { currentUserId: string; principalIds: string[] }) => {
			const principalIdsToRemove = principalIds.filter((id) => id !== currentUserId);

			setLiveSpecificAccessDelta((prev) => removeAllPrincipalsFromLiveDelta({ liveDelta: prev }));

			await withSaving(currentUserId, () =>
				triggerLiveMutation({ type: 'remove-all', principalIdsToRemove }),
			);
		},
		[setLiveSpecificAccessDelta, triggerLiveMutation, withSaving],
	);

	return { onChangeRole, onRemovePrincipal, onRemoveAllPrincipals };
}
